import IconConnectors from '@apollo/icons/default/IconConnectors.svg';
import IconError from '@apollo/icons/default/IconError.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconRouter from '@apollo/icons/default/IconRouter.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import React from 'react';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { usePublishedSubgraphs } from 'src/hooks/usePublishedSubgraphs';
import { ErrorInsightsListGroupByColumn } from 'src/lib/graphqlTypes/types';

import { BreakdownOption, GroupOption } from './GroupByButton';
import { wellKnownErrorCodes } from './wellKnownErrorCodes';

export function CodeInfoIcon({ code }: { code: string }) {
  return wellKnownErrorCodes.has(code) ? (
    <Tooltip
      className="whitespace-normal"
      label={wellKnownErrorCodes.get(code)}
      hasArrow
      placement="top"
      gutter={20}
      interactive
    >
      <IconInfo className="icon-secondary shrink-0" />
    </Tooltip>
  ) : null;
}

export function ServiceIcon({ serviceName }: { serviceName: string }) {
  const graphRef = useGraphRef();
  const { subgraphMetaHelpers } = usePublishedSubgraphs({
    graphId: graphRef?.graphId,
    graphVariant: graphRef?.graphVariant,
  });
  if (
    !serviceName ||
    serviceName.toLowerCase().startsWith('router') ||
    serviceName.toLowerCase().startsWith('apollo-router@')
  ) {
    return (
      <Tooltip
        label="Router, Gateway, or GraphQL server"
        placement="top"
        hasArrow
      >
        <IconRouter className="icon-secondary" />
      </Tooltip>
    );
  }
  const subgraphEntry = subgraphMetaHelpers?.subgraphMap[serviceName];
  if (!subgraphEntry) {
    return null;
  }
  if (subgraphEntry?.isConnector) {
    return (
      <Tooltip label="Connector subgraph service" placement="top" hasArrow>
        <IconConnectors className="icon-secondary" />
      </Tooltip>
    );
  } else {
    return (
      <Tooltip label="GraphQL subgraph service" placement="top" hasArrow>
        <IconSingleService className="icon-secondary" />
      </Tooltip>
    );
  }
}

export function GroupIcon({
  groupByDimension,
  dimensionValue,
}: {
  groupByDimension: GroupOption | BreakdownOption;
  dimensionValue: string;
}) {
  switch (groupByDimension) {
    case ErrorInsightsListGroupByColumn.SERVICE_OR_AGENT:
      return <ServiceIcon serviceName={dimensionValue} />;
    case ErrorInsightsListGroupByColumn.CODE:
      return <IconError className="icon-error shrink-0 align-middle" />;
    case ErrorInsightsListGroupByColumn.PATH:
      return null;
    default:
      return null;
  }
}

export function GroupRightIcon({
  groupByDimension,
  dimensionValue,
}: {
  groupByDimension: GroupOption | BreakdownOption;
  dimensionValue: string;
}) {
  switch (groupByDimension) {
    case ErrorInsightsListGroupByColumn.CODE:
      return <CodeInfoIcon code={dimensionValue} />;
    default:
      return null;
  }
}
